import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ReCAPTCHA from 'react-google-recaptcha';
import { useHistory } from 'react-router-dom';

import ErrorMessage from '@/components/atoms/ErrorMessage';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Form/Input';
import { Routes } from '@/core/router/routes.types';
import { useForgotPasswordMutation } from '@/services/innovorder/auth/auth.endpoints';
import { ENV } from '@/utils/env';
import { getErrorCode, stringifyError } from '@/utils/errors';
import {
    Actions,
    Card,
    CaptchaWrapper,
    InputWrapper,
    Page,
    SecondaryLink,
    Subtitle,
    SuccessMessage,
    Title,
} from './ForgotPassword.style';

const ForgotPassword: React.FunctionComponent = () => {
    const history = useHistory();
    const siteKey = ENV.RECAPTCHA_SITE_KEY;
    const captchaRef = useRef<ReCAPTCHA>(null);

    const [email, setEmail] = useState('');
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);

    const [forgotPassword, { isLoading, error, isSuccess }] = useForgotPasswordMutation();
    const errorCode = error ? getErrorCode(error) : null;

    const handleSubmit = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault();
            if (!email || !captchaToken) return;
            try {
                await forgotPassword({ email, 'g-recaptcha-response': captchaToken }).unwrap();
            } catch {
                // Error rendered below via `error` state
                captchaRef.current?.reset();
                setCaptchaToken(null);
            }
        },
        [email, captchaToken, forgotPassword],
    );

    if (!siteKey) {
        return (
            <Page>
                <Card onSubmit={(e) => e.preventDefault()}>
                    <Title>
                        <FormattedMessage id="forgotPassword.title" />
                    </Title>
                    <ErrorMessage>
                        <FormattedMessage id="forgotPassword.recaptchaNotConfigured" />
                    </ErrorMessage>
                </Card>
            </Page>
        );
    }

    if (isSuccess) {
        return (
            <Page>
                <Card onSubmit={(e) => e.preventDefault()}>
                    <Title>
                        <FormattedMessage id="forgotPassword.successTitle" />
                    </Title>
                    <SuccessMessage>
                        <FormattedMessage id="forgotPassword.successMessage" />
                    </SuccessMessage>
                    <Actions>
                        <Button buttonType="primary" fullWidth thick onClick={() => history.push(Routes.Login)}>
                            <FormattedMessage id="forgotPassword.backToLogin" />
                        </Button>
                    </Actions>
                </Card>
            </Page>
        );
    }

    const errorLabel =
        errorCode === 'captcha_error' ? (
            <FormattedMessage id="forgotPassword.recaptchaFailed" />
        ) : error ? (
            stringifyError(error)
        ) : null;

    return (
        <Page>
            <Card onSubmit={handleSubmit}>
                <Title>
                    <FormattedMessage id="forgotPassword.title" />
                </Title>
                <Subtitle>
                    <FormattedMessage id="forgotPassword.subtitle" />
                </Subtitle>
                <InputWrapper>
                    <Input
                        type="email"
                        value={email}
                        onChange={(event): unknown => setEmail(event.target.value.trim())}
                        placeholder="email@innovorder.fr"
                    />
                </InputWrapper>

                <CaptchaWrapper>
                    <ReCAPTCHA ref={captchaRef} sitekey={siteKey} onChange={setCaptchaToken} />
                </CaptchaWrapper>

                {errorLabel ? <ErrorMessage>{errorLabel}</ErrorMessage> : null}

                <Actions>
                    <Button
                        buttonType="primary"
                        fullWidth
                        thick
                        disabled={!email || !captchaToken || isLoading}
                        onClick={handleSubmit}
                    >
                        <FormattedMessage id="forgotPassword.submit" />
                    </Button>
                    <SecondaryLink type="button" onClick={() => history.push(Routes.Login)}>
                        <FormattedMessage id="forgotPassword.backToLogin" />
                    </SecondaryLink>
                </Actions>
            </Card>
        </Page>
    );
};

export default ForgotPassword;
